/*
 * DeclaracionAnual.js — Pestaña "Declaración anual" (C-020, ver diseno/CAMBIOS.md).
 *
 * Un ejercicio a la vez (el de la barra: "Ejercicio 2025"). Tarjetas de morales y físicas, embudo
 * "Detenidos en", filtros (buscar, contador, Atrasadas, Por revisar) y la tabla agrupada con los
 * cuatro pasos. La lógica pura está en anual.js; las reglas también se validan en el servidor.
 *
 * props:
 *   ejercicio, api { listar(ejercicio), guardar(clienteId, ejercicio, paso, estado) }
 *   esContador, marcaRevisado (Supervisor), muestraPorRevisar (Dueño y Supervisor)
 *   Icono, Sello          componentes del círculo y del sello de revisión (App.js)
 *   onAbrirCliente(id), onEditarCliente(id) | null
 *   clienteInicial        filtra a un cliente (desde "Ver en la tabla →" de la ficha)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Clock, Search } from 'lucide-react';
import { avisar, avisarError } from './Dialogo';
import CeldaCliente from './CeldaCliente';
import FiltroMenu from './FiltroMenu';
import { fechaCorta, normalizarNombre } from './estados';
import {
  ANUAL_TERMINADAS, PASOS_ANUAL, diasAtrasoAnual, fechaLimiteAnual, pasoActualAnual, pasoTerminado,
  pasoVisibleAnual, porRevisarAnual, resumenGrupo, siguienteEstadoAnual,
} from './anual';

const GRUPOS = [['moral', 'Personas morales'], ['fisica', 'Personas físicas']];

export default function DeclaracionAnual({
  ejercicio, api, esContador, marcaRevisado, muestraPorRevisar, Icono, Sello, onAbrirCliente, onEditarCliente,
  clienteInicial = null,
}) {
  const [datos, setDatos] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [contadores, setContadores] = useState([]);
  const [paso, setPaso] = useState(null); // índice 0-3, 'terminadas' o null
  const [atrasadas, setAtrasadas] = useState(false);
  const [porRevisar, setPorRevisar] = useState(false);
  const [soloCliente, setSoloCliente] = useState(clienteInicial);
  const hoy = new Date();

  useEffect(() => { setSoloCliente(clienteInicial); }, [clienteInicial]);

  const cargar = useCallback(async () => {
    try {
      setDatos(await api.listar(ejercicio));
    } catch (err) {
      setDatos({ clientes: [], resico: 0 });
      avisarError('cargar la declaración anual', err);
    }
  }, [api, ejercicio]);

  useEffect(() => { setDatos(null); cargar(); }, [cargar]);

  const todos = useMemo(() => (datos ? datos.clientes : []), [datos]);
  const opcionesContador = useMemo(() => {
    const vistos = new Map();
    todos.forEach(c => { if (c.contador_id) vistos.set(String(c.contador_id), c.contador_nombre); });
    return [...vistos.entries()].sort((a, b) => (a[1] || '').localeCompare(b[1] || '')).map(([valor, texto]) => {
      const suyos = todos.filter(c => String(c.contador_id) === valor);
      const atr = suyos.filter(c => diasAtrasoAnual(c, ejercicio, hoy) > 0).length;
      return { valor, texto, corto: (texto || '').split(' ')[0], detalle: `${suyos.length} ${suyos.length === 1 ? 'cliente' : 'clientes'}${atr ? ` · ${atr} atrasados` : ''}` };
    });
  }, [todos, ejercicio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Base: búsqueda y contador (y el cliente si viene de la ficha). Sobre ella cuentan embudo y chips.
  const q = normalizarNombre(busqueda.trim());
  const base = todos.filter(c =>
    (!soloCliente || c.id === soloCliente) &&
    (!contadores.length || contadores.includes(String(c.contador_id))) &&
    (!q || normalizarNombre(c.nombre).includes(q)));
  const detenidoEn = (c) => { const i = pasoActualAnual(c); return i >= PASOS_ANUAL.length ? ANUAL_TERMINADAS : i; };
  const coincidePaso = (c) => paso === null || detenidoEn(c) === paso;
  const atrasadaC = (c) => diasAtrasoAnual(c, ejercicio, hoy) > 0;
  const visibles = base.filter(c => coincidePaso(c) && (!atrasadas || atrasadaC(c)) && (!porRevisar || porRevisarAnual(c)));
  if (atrasadas) visibles.sort((a, b) => diasAtrasoAnual(b, ejercicio, hoy) - diasAtrasoAnual(a, ejercicio, hoy));
  const totalAtrasadas = base.filter(c => coincidePaso(c) && atrasadaC(c)).length;
  const totalPorRevisar = base.filter(c => coincidePaso(c) && porRevisarAnual(c)).length;
  const embudo = [...PASOS_ANUAL.map((p, i) => ({ valor: i, corto: p.corto })), { valor: ANUAL_TERMINADAS, corto: 'Terminadas' }]
    .map(e => ({ ...e, n: base.filter(c => detenidoEn(c) === e.valor).length, atr: base.filter(c => detenidoEn(c) === e.valor && atrasadaC(c)).length }));
  const filtrosPuestos = [paso !== null, atrasadas, porRevisar, contadores.length > 0, !!q, !!soloCliente].filter(Boolean).length;
  const limpiar = () => { setPaso(null); setAtrasadas(false); setPorRevisar(false); setContadores([]); setBusqueda(''); setSoloCliente(null); };

  const plazoVencido = hoy > fechaLimiteAnual('moral', ejercicio);

  const cambiar = async (cliente, clavePaso) => {
    const nuevo = siguienteEstadoAnual(cliente, clavePaso, marcaRevisado);
    const antes = datos;
    const pasosNuevos = { ...cliente.pasos, [clavePaso]: { estado: nuevo, fecha_actualizacion: new Date().toISOString(), usuario_nombre: null } };
    if (clavePaso === 'presentada' && nuevo === 'rosa') pasosNuevos.pago = { estado: 'no_aplica', fecha_actualizacion: new Date().toISOString() };
    if (clavePaso === 'presentada' && cliente.pasos?.presentada?.estado === 'rosa') pasosNuevos.pago = { estado: 'rojo' };
    setDatos(d => ({ ...d, clientes: d.clientes.map(c => (c.id === cliente.id ? { ...c, pasos: pasosNuevos } : c)) }));
    try {
      await api.guardar(cliente.id, ejercicio, clavePaso, nuevo);
      if (clavePaso === 'calculo' && nuevo === 'azul') avisar({ texto: `Cálculo de ${cliente.nombre} revisado.` });
      cargar();
    } catch (err) {
      setDatos(antes);
      avisarError('cambiar el estado', err);
    }
  };

  const celda = (c, p) => {
    const v = pasoVisibleAnual(c, p.clave, ejercicio, hoy);
    const guardado = c.pasos && c.pasos[p.clave];
    const quien = (guardado?.usuario_nombre || '').trim().split(/\s+/)[0];
    const ayuda = [v.tooltip, guardado?.fecha_actualizacion && v.clave !== 'bloqueado'
      ? `actualizado el ${fechaCorta(guardado.fecha_actualizacion, hoy)}${quien ? ` por ${quien}` : ''}` : null].filter(Boolean).join(' · ');
    const deshabilitado = v.clave === 'bloqueado' || v.fijo;
    return (
      <td key={p.clave} className="cell-estado">
        <div className="estado-container-table">
          {v.noAplica ? (
            <button
              type="button"
              className="anual-no-aplica"
              data-tooltip={ayuda}
              aria-label={`${p.nombre} de ${c.nombre}: ${ayuda}`}
              disabled={v.fijo}
              onClick={() => cambiar(c, p.clave)}
            >
              No aplica
            </button>
          ) : (
            <span className="estado-circulo-wrap">
              <button
                type="button"
                className={`estado-circle ${v.clave}${v.atrasado ? ' atrasado' : ''}`}
                data-tooltip={ayuda}
                aria-label={`${p.nombre} de ${c.nombre}: ${ayuda}`}
                disabled={deshabilitado}
                onClick={() => cambiar(c, p.clave)}
              >
                <Icono clave={v.clave} />
              </button>
              <Sello tipo={v.sello} />
              {v.atrasado && <span className="atraso-badge" aria-hidden="true">+{v.dias}d</span>}
            </span>
          )}
          {v.nota && <span className="estado-fecha-table">{v.nota}</span>}
          {v.esperaRevision && <span className="estado-fecha-table espera-revision">Espera revisión</span>}
        </div>
      </td>
    );
  };

  if (datos === null) return <p className="obl-vacio">Cargando…</p>;

  return (
    <div className="anual">
      <div className="obl-herramientas">
        <label className="obl-buscar">
          <Search size={14} aria-hidden="true" />
          <input type="search" placeholder="Buscar cliente…" aria-label="Buscar cliente" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </label>
        {!esContador && opcionesContador.length > 1 && (
          <FiltroMenu etiqueta="Contador" opciones={opcionesContador} seleccion={contadores} onCambiar={setContadores} />
        )}
        <button type="button" className="pastilla atrasados" aria-pressed={atrasadas} onClick={() => setAtrasadas(v => !v)}>
          {atrasadas ? <Check size={14} aria-hidden="true" /> : <Clock size={14} aria-hidden="true" className="icono-rojo" />}
          Atrasadas <span className="pastilla-num rojo">{totalAtrasadas}</span>
        </button>
        {muestraPorRevisar && (
          <button type="button" className="pastilla por-revisar" aria-pressed={porRevisar} onClick={() => setPorRevisar(v => !v)}>
            {porRevisar ? <Check size={14} aria-hidden="true" /> : <Clock size={14} aria-hidden="true" />}
            Por revisar <span className="pastilla-num">{totalPorRevisar}</span>
          </button>
        )}
        {soloCliente && (
          <button type="button" className="pastilla prendida" onClick={() => setSoloCliente(null)}>
            Solo {todos.find(c => c.id === soloCliente)?.nombre || 'este cliente'} ×
          </button>
        )}
      </div>

      <div className="embudo-fila anual-embudo" role="group" aria-label={`Detenidos en cada paso, ejercicio ${ejercicio}`}>
        <span className="embudo-titulo">Detenidos en:</span>
        {embudo.map(e => (
          <div key={e.valor} className={`embudo-paso${e.valor === ANUAL_TERMINADAS ? ' terminados' : ''}${paso === e.valor ? ' seleccionado' : ''}`}>
            <button type="button" className="embudo-paso-btn" aria-pressed={paso === e.valor} onClick={() => setPaso(paso === e.valor ? null : e.valor)}>
              <span className={`embudo-cuantos${e.n === 0 ? ' cero' : ''}`}>{e.n}</span>
              <span className="embudo-nombre">{e.corto}</span>
            </button>
            {e.atr > 0 && <span className="embudo-atrasados" aria-label={`${e.atr} atrasadas`}>{e.atr}</span>}
          </div>
        ))}
      </div>

      {!plazoVencido ? (
        <p className="anual-aviso" role="status">
          El ejercicio {ejercicio} se presenta en {ejercicio + 1}: personas morales a más tardar el {fechaCorta(fechaLimiteAnual('moral', ejercicio), new Date(0))} y físicas el {fechaCorta(fechaLimiteAnual('fisica', ejercicio), new Date(0))}.
        </p>
      ) : null}

      <div className="anual-tarjetas">
        {GRUPOS.map(([tipo, titulo]) => {
          const r = resumenGrupo(base.filter(c => c.tipo === tipo), ejercicio, hoy);
          return (
            <section key={tipo} className="anual-tarjeta">
              <h3>{titulo}</h3>
              <span className="anual-limite">A más tardar {fechaCorta(fechaLimiteAnual(tipo, ejercicio), new Date(0))}</span>
              <div className="anual-nums">
                <div><small>Presentadas</small><b>{r.presentadas} de {r.total}</b></div>
                <div><small>Enviadas</small><b>{r.enviadas} de {r.total}</b></div>
                <div><small>Pagadas o sin pago</small><b>{r.pagadas} de {r.total}</b></div>
                {r.porRevisar > 0 && <div className="rev"><small>Por revisar</small><b>{r.porRevisar}</b></div>}
                {r.atrasadas > 0 && <div className="atr"><small>Atrasadas</small><b>{r.atrasadas}</b></div>}
              </div>
            </section>
          );
        })}
      </div>

      <ul className="anual-leyenda" aria-label="Leyenda">
        <li><span className="estado-circle completado" aria-hidden="true"><Icono clave="completado" size={11} /></span>Completado</li>
        <li><span className="estado-circulo-wrap"><span className="estado-circle completado" aria-hidden="true"><Icono clave="completado" size={11} /></span><Sello tipo="por-revisar" /></span>Por revisar</li>
        <li><span className="estado-circulo-wrap"><span className="estado-circle revisado" aria-hidden="true"><Icono clave="revisado" size={11} /></span><Sello tipo="revisado" /></span>Revisado</li>
        <li><span className="estado-circle presentado-cero" aria-hidden="true"><Icono clave="presentado-cero" size={11} /></span>Presentada en $0</li>
        <li><span className="anual-no-aplica leyenda">No aplica</span>Sin pago este año</li>
      </ul>

      <div className="table-wrapper">
        <table className="data-table anual-tabla">
          <thead>
            <tr>
              <th className="col-client" scope="col">Cliente</th>
              <th className="col-proceso" scope="col">Cálculo<small className="col-proceso-limite">revisa Pedro si aplica</small></th>
              <th className="col-proceso" scope="col">Presentada<small className="col-proceso-limite">en el SAT</small></th>
              <th className="col-proceso" scope="col">Enviada al cliente</th>
              <th className="col-proceso" scope="col">Pago<small className="col-proceso-limite">si sale a pagar</small></th>
            </tr>
          </thead>
          <tbody>
            {visibles.length === 0 ? (
              <tr>
                <td colSpan={5} className="filtros-vacio">
                  <b>{todos.length === 0 ? 'Ningún cliente presenta declaración anual.' : filtrosPuestos > 1 ? 'Ningún cliente cumple todos los filtros.' : 'Ningún cliente coincide con este filtro.'}</b>
                  {filtrosPuestos > 0 && <button type="button" className="filtros-limpiar" onClick={limpiar}>Limpiar filtros</button>}
                </td>
              </tr>
            ) : GRUPOS.map(([tipo, titulo]) => {
              const filas = visibles.filter(c => c.tipo === tipo);
              if (!filas.length) return null;
              return [
                <tr key={`g-${tipo}`} className="section-header">
                  <td colSpan={5}>{titulo} · {filas.length} · a más tardar {fechaCorta(fechaLimiteAnual(tipo, ejercicio), new Date(0))}</td>
                </tr>,
                ...filas.map(c => (
                  <tr key={c.id}>
                    <td className="client-name">
                      <CeldaCliente
                        cliente={c}
                        onAbrir={() => onAbrirCliente && onAbrirCliente(c.id)}
                        onEditar={onEditarCliente ? () => onEditarCliente(c.id) : null}
                        mostrarContador={contadores.length !== 1}
                        avance={{
                          terminados: PASOS_ANUAL.filter(p => pasoTerminado(c, p.clave)).length,
                          total: PASOS_ANUAL.length,
                          ayuda: `${PASOS_ANUAL.filter(p => pasoTerminado(c, p.clave)).length} de 4 pasos terminados.`,
                        }}
                      >
                        {c.revision_pedro !== 'no' && <span className="etiqueta-revision">Pedro revisa</span>}
                      </CeldaCliente>
                    </td>
                    {PASOS_ANUAL.map(p => celda(c, p))}
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </div>
      {datos.resico > 0 && (
        <p className="anual-pie">
          {datos.resico} {datos.resico === 1 ? 'cliente en RESICO no aparece' : 'clientes en RESICO no aparecen'}: no presentan declaración anual.
        </p>
      )}
    </div>
  );
}
